<template>
  <div
    class="question"
    :class="{ selected: isSelected, odd: isOdd, static: isStatic, editable: isEditable }"
  >
    <div class="header" @click="handleSelect">
      <div class="question-title">
        <span class="order-indicator" v-if="!isStatic">
          {{ index + 1 }}.
          <span class="conditional" v-if="value.visibilityCondition">?</span>
        </span>
        <span class="order-indicator" v-else>1.</span>
        {{ value.title }}
      </div>
      <div class="tools">
        <div class="question-type">{{ friendlyQuestionType }}</div>
        <div class="controls" v-if="!isStatic">
          <span v-if="isEditable">
            <span class="option">
              <el-tooltip content="Conditional">
                <i
                  class="fa fa-fw fa-link"
                  :class="{ 'has-conditional': isConditional(value) }"
                  @click="handleSetConditional(value)"
                ></i>
              </el-tooltip>
            </span>
            <span class="tool-divider"></span>
            <span class="option">
              <el-tooltip content="Edit">
                <i class="fa fa-fw fa-pencil" @click="handleEdit"></i>
              </el-tooltip>
            </span>
            <span class="option">
              <el-tooltip content="Remove">
                <i class="fa fa-fw fa-trash" @click="handleRemove"></i>
              </el-tooltip>
            </span>
            <el-divider direction="vertical" v-if="isEditableQuestion"></el-divider>
          </span>
          <span class="collapse-toggler option" v-if="isEditableQuestion">
            <i
              class="fa fa-fw"
              :class="isCollapsed ? 'fa-angle-down' : 'fa-angle-up'"
              @click="handleToggleCollapse"
            ></i>
          </span>
        </div>
      </div>
    </div>
    <div class="body" v-if="isEditableQuestion">
      <div v-for="(editor, index) in editors" :key="index">
        <component
          v-if="editor.condition(value)"
          v-model="computedValue"
          :is="editor.component"
          :isCollapsed="isCollapsed"
          :isEditable="isEditable"
          @onToggleCollapse="handleToggleCollapse"
        ></component>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import draggable from 'vuedraggable';

import Empty from '@/components/common/Empty';
import * as surveyBuilderTypes from '@/store/modules/surveybuilder/mutation-types';

import ScaleEditor from './scale/ScaleEditor';
import SelectEditor from './select/SelectEditor';
import MusicEditor from './music/MusicEditor';

export default {
  name: 'QuestionEditor',
  components: {
    draggable,

    Empty,

    ScaleEditor,
    SelectEditor,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      default: 0,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isStatic: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    choicesList: [],
    isCollapsed: true,
  }),
  computed: {
    ...mapState('surveybuilder', ['survey', 'questionTypeList']),

    editors() {
      return [
        {
          component: ScaleEditor,
          condition: q => q.questionType === 'SCALE',
        },
        {
          component: SelectEditor,
          condition: q =>
            ['SELECT', 'MULTI_SELECT', 'RADIOGROUP', 'SORTABLE'].includes(q.questionType),
        },
        {
          component: MusicEditor,
          condition: q => q.questionType === 'MEDIA',
        },
      ];
    },

    friendlyQuestionType() {
      const questionType = this.questionTypeList.find(x => x.value === this.value.questionType);
      return questionType.title || 'Unknown';
    },

    isEditableQuestion() {
      const editable = ['SCALE', 'SELECT', 'MULTI_SELECT', 'RADIOGROUP', 'SORTABLE', 'MEDIA'];
      return editable.includes(this.value.questionType);
    },

    isOdd() {
      return !this.isSelected && this.index % 2 !== 0;
    },

    computedValue: {
      get() {
        return this.value;
      },
      set(item) {
        this.$emit('input', item);
      },
    },
    computedChoices: {
      get() {
        return this.choicesList;
      },
      set(items) {
        this.$emit('input', items);
      },
    },
  },
  methods: {
    isConditional(question) {
      return question.visibilityCondition && question.visibilityCondition.trim() !== '';
    },

    handleSetConditional(question) {
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_QUESTION_CONDITIONAL_LOGIC_DIALOG_QUESTION}`,
        question,
      );
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_QUESTION_CONDITIONAL_LOGIC_DIALOG_VISIBILITY}`,
        true,
      );
    },

    handleSelect() {
      this.$emit('onSelect');
    },

    handleRemove() {
      this.$emit('onRemove');
    },

    handleToggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    handleEdit() {
      this.$emit('onEdit');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/style/_variables_live.scss';

.question {
  padding: 0;
  margin: 12px;
  border: 1px solid $border-color;
  border-radius: 3px;
}

.question.selected {
  border: 1px solid $colony-primary;
}

.question.selected .header {
  background-color: $colony-primary;
  border-bottom: 1px solid $colony-primary;
  color: $colony-light-gray;
}

.question .header {
  font-size: 1.2em;
  padding: 6px 8px;
  border-bottom: 1px solid $border-color;
  display: flex;
}

.question.editable .header {
  cursor: grab;
}

.static {
  background-color: lighten($colony-light-gray, 5%);
}

.static .header {
  cursor: default;
  font-style: italic;
  color: darken($colony-light-gray, 15%);
}

.static .tools {
  color: darken($colony-light-gray, 15%) !important;
}

.question .question-title {
  flex-grow: 2;
  display: flex;
  align-items: center;
}

.question .conditional {
  margin-left: 8px;
  font-size: 1.2em;
  color: $colony-orange;
}

.has-conditional {
  color: $colony-success;
}

.question .tools {
  color: $colony-light-gray;
  text-align: right;
  white-space: nowrap;
  margin-left: 10px;
}

.question.odd .tools {
  color: darken($colony-light-gray, 15%);
}

.collapse-toggler {
  margin-left: 12px;
}

.question.odd {
  background-color: lighten($colony-light-gray, 5%);
}

.question .body {
  background-color: $white;
  padding: 6px 8px;
}

.question-type {
  font-size: 0.5em;
  text-transform: uppercase;
}

.order-indicator {
  width: 3em;
  font-size: 1.1em;
  font-weight: bolder;
}

.empty-container {
  font-size: 0.7em;
  border: 2px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.option {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// QuestionEditor.vue?35828396